<template>
  <div
    id="paypal-gate"
    class="paypal-box anim-fade-in"
  >
    <GwMessage
      v-if="paypalError"
      :message="{
        hide: true,
        type: 'error',
        text: $t('payment.error.status_UNKNOWN_ERROR'),
      }"
      class="mb-4"
    >
      <template #default>
        <span>
          {{ $t('payment.error.status_UNKNOWN_ERROR') }}
          <span>
            <OpenChatLink />
          </span>
        </span>
      </template>
    </GwMessage>

    <div
      v-show="paypalBtnLoaded"
      id="paypal-button-container"
      class="paypal-box_button-container anim-fade-in_slow"
    ></div>

    <v-progress-circular
      v-if="loading || onApproveLoader"
      :size="62"
      data-tst="table_loader"
      :model-value="80"
      :indeterminate="true"
      class="mx-auto"
    ></v-progress-circular>

    <div class="paypal-box_slot-content">
      <slot
        name="text"
        :tierPlan="selectedTierPlan"
      />
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import GwMessage from '@/components/Messages/GwMessage'
// https://developer.paypal.com/docs/subscriptions/integrate/#create-the-subscription
// https://developer.paypal.com/docs/subscriptions/reference/handle-errors/
// staging-user: sb-cgbn475182828@personal.example.com
// staging-pass: [&utsG6Y

import payment_mix from '@/mixins/payment_mix'
import { seg_track, seg_getProduct, seg_getTotalAndCurrency } from '@/helpers/segment'
import links_mix from '@/mixins/links_mix'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'
import { logSentry } from '@gto/sentry'
import { loadScript, unloadScript } from 'vue-plugin-load-script'

export default {
  name: 'PaypalGateway',
  components: {
    OpenChatLink,
    GwMessage,
  },
  mixins: [payment_mix, links_mix],
  data() {
    return {
      paypalBtnLoaded: false,
      paypalError: false,
      onApproveLoader: false,
      customGame: 'CASH',
    }
  },
  created() {
    this.initialize()
  },
  methods: {
    initialize() {
      loadScript(this.sdkSrc)
        .then(() => {
          if (window.paypal) {
            this.createPaypalBtn()
          } else {
            this.handleError('paypal-not-available 1')
            unloadScript(this.sdkSrc)
          }
        })
        .catch(() => {
          this.handleError('paypal-not-available 2')
          unloadScript(this.sdkSrc)
        })
    },
    createPaypalBtn() {
      this.paypalBtnLoaded = true
      this.paypalError = false
      const {
        selectedPlan,
        externalPlanID,
        $store,
        handleError,
        removePaypalBtn,
        showOnApproveLoader,
        user_info,
        segmentTrackCheckout,
        segmentTrackSuccess,
        onSuccessPayment,
      } = this
      const checkout_id = window.seg_checkout_id

      const options = {
        plan_id: externalPlanID,
        custom_id: user_info.public_id,
      }

      window.paypal
        .Buttons({
          style: {
            layout: 'horizontal',
            color: 'gold',
            label: 'pay',
          },
          createSubscription(data, actions) {
            return actions.subscription.create(options)
          },
          async onApprove(data, actions) {
            try {
              segmentTrackCheckout(checkout_id)
              segmentTrackSuccess()
            } catch (error) {
              console.log('PAYPAL-SEGMENT-ERROR')
            }

            showOnApproveLoader()
            removePaypalBtn()
            const res = await $store.dispatch('api_call', {
              method: 'post',
              endpoint: 'payments/subscription/paypal/confirm/',
              data: { subscription_id: data.subscriptionID },
            })
            if (res.response?.status === 200 || res.response?.status === 204) {
              await $store.dispatch('api_call', {
                method: 'get',
                endpoint: 'account/settings/',
                hasCancel: false,
                mutation: 'api_account_settings',
                action: 'updateUserInfo',
              })
              const plan = selectedPlan

              localStorage.setItem('gtm_payment_session', Date.now())

              onSuccessPayment({
                payment_provider: 'paypal',
                payment_method: 'paypal',
              })
            } else {
              handleError(`gto-server responded with status -- ${res.response.status}`)
            }
          },
          onError(err) {
            handleError(err)
          },
        })
        .render('#paypal-button-container')
    },
    recreatePaypalBtn() {
      const paypalButtonContainer = document.createElement('div')
      paypalButtonContainer.id = 'paypal-button-container'
      paypalButtonContainer.className = 'paypal-box_button-container'

      this.removePaypalBtn()
      document.getElementById('paypal-gate').appendChild(paypalButtonContainer)
      nextTick(() => {
        this.createPaypalBtn()
      })
    },
    removePaypalBtn() {
      try {
        const elem = document.getElementById('paypal-button-container')
        elem.parentNode.removeChild(elem)
      } catch (error) {
        //
      }
      console.log('paypal btn refreshed')
    },
    showOnApproveLoader() {
      this.onApproveLoader = true
    },
    handleError(error) {
      this.onApproveLoader = false
      this.paypalError = true
      logSentry('Paypal Gateway: Payment failed', {
        error: JSON.stringify(error, null, 2),
        email: this.$store.getters.user?.email,
        selectedPlan: this.selectedTierPlan?.full_name,
        currentPlan: this.currentTierPlan?.full_name,
      })
    },
    segmentTrackCheckout(checkout_id) {
      seg_track('payment_info_entered', {
        checkout_id,
      })
    },
    segmentTrackSuccess() {
      const product = {
        plan: this.selectedTierPlan?.plan,
        price: { price: this.selectedTierPlan?.plan?.price },
      }
      seg_track('checkout_step_completed', {
        step: 2,
        checkout_type: 'Purchase Funnel',
        checkout_id: window.seg_checkout_id,
        payment_method: 'PayPal',
        // currency: 'USD',
        ...seg_getTotalAndCurrency([product]),
        products: [seg_getProduct(product)],
      })
    },
  },
  computed: {
    user_info() {
      const { user_info } = this.$store.getters
      return user_info
    },
    sdkSrc() {
      const res = `https://www.paypal.com/sdk/js?client-id=${
        import.meta.env.VITE_PAYPAL_ID
      }&vault=true&intent=subscription&disable-funding=credit,card`
      return res
    },
    tierOptions() {
      return this.$store.getters.tierOptions(this)
    },
    selectedPayPlan() {
      const { payplan } = this.$route.query
      let option
      this.tierOptions.forEach(i =>
        i.options.forEach(o => {
          if (o.id === payplan) {
            option = o
          }
        }),
      )
      return option
    },
    externalPlanID() {
      return this.selectedPlan?.external_plans?.paypal?.external_plan_id
    },
    loading() {
      return this.$store.getters.api_get('api_payments_subscription_paypal')?.loading
    },
  },
  watch: {
    externalPlanID() {
      this.recreatePaypalBtn()
    },
  },
}
</script>

<style lang="scss">
@media (max-width: 1023px) {
  .paypal-box {
    min-width: unset !important;
  }
}

.paypal-button-text {
  color: white !important;
  // background: orange;
}
.paypal-box {
  min-width: 544px;
  display: flex;
  flex-direction: column;
  padding: 2rem 0 2rem 0;
  &_button-container {
    order: 1;
    // min-height: 52px;
    border-radius: 4px;
    padding: 1rem;
    min-height: 122px;
    background: var(--clr-white-0);
    width: 100%;
  }
  &_slot-content {
    order: 2;
  }
}
</style>
